import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import firebase from "firebase/app";
import "firebase/firestore";
import Cookie from "js-cookie";
import "./SubscriptionManagement.css";

class SubscriptionManagement extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPlan: "basic",
      endDate: "",
      startDate: "",
      autoRenew: false,
      loading: true,
      showCancelConfirm: false,
      paymentHistory: [],
      error: null,
      userInfo: null
    };
  }

  componentDidMount() {
    this.loadSubscriptionData();
  }

  loadSubscriptionData = async () => {
    
    // Mettre l'état en loading
    this.setState({ loading: true, error: null });

    try {
      const db = firebase.firestore();
      let userDoc = null;
      let foundMethod = null;

      // MÉTHODE ROBUSTE : Recherche multi-critères
      
      // Méthode 1: Session key depuis les cookies
      const sessionKey = Cookie.get('session_key');
      if (sessionKey) {
        const sessionQuery = await db.collection('users')
          .where('randomString', '==', sessionKey)
          .limit(1)
          .get();
        
        if (!sessionQuery.empty) {
          userDoc = sessionQuery.docs[0];
          foundMethod = "session_key";
        }
      }

      // Méthode 2: Données Redux/Firebase + localStorage
      if (!userDoc) {
        const { user, profile } = this.props;
        
        // Essayer localStorage
        const lastLoggedUser = localStorage.getItem('lastLoggedUser');
        let localUserData = null;
        if (lastLoggedUser) {
          try {
            localUserData = JSON.parse(lastLoggedUser);
          } catch (e) {
          }
        }
        
        const activeUserEmail = user?.email || profile?.email || localUserData?.email;
        const activeUserId = user?.uid || profile?.uid || localUserData?.uid;

        // Recherche par userId
        if (activeUserId) {
          userDoc = await db.collection('users').doc(activeUserId).get();
          if (userDoc.exists) {
            foundMethod = "userId";
          }
        }

        // Recherche par email
        if (!userDoc?.exists && activeUserEmail) {
          const emailQuery = await db.collection('users')
            .where('email', '==', activeUserEmail)
            .limit(1)
            .get();
          
          if (!emailQuery.empty) {
            userDoc = emailQuery.docs[0];
            foundMethod = "email";
          }
        }
      }

      // Méthode 3: Fallback - utilisateur récent
      if (!userDoc) {
        try {
          const recentDate = new Date(Date.now() - 24 * 60 * 60 * 1000);
          const recentQuery = await db.collection('users')
            .where('lastAccess', '>=', recentDate)
            .orderBy('lastAccess', 'desc')
            .limit(1)
            .get();
          
          if (!recentQuery.empty) {
            userDoc = recentQuery.docs[0];
            foundMethod = "recent_access";
          }
        } catch (indexError) {
          // Essayons une recherche générale comme dernière option
          try {
            const generalQuery = await db.collection('users')
              .where('role', '==', 'user')
              .limit(1)
              .get();
            
            if (!generalQuery.empty) {
              userDoc = generalQuery.docs[0];
              foundMethod = "general_fallback";
            }
          } catch (e) {
          }
        }
      }

      if (!userDoc?.exists) {
        throw new Error("Aucun utilisateur trouvé dans la base de données.");
      }

      const userData = userDoc.data();
      const subscription = userData.subscription || {};

      // Calculer les détails de l'abonnement
      let status = 'Inactif';
      let daysRemaining = 0;
      let endDateFormatted = 'Non défini';
      let startDateFormatted = 'Non défini';

      if (subscription.endDate) {
        const endDate = subscription.endDate.toDate ? subscription.endDate.toDate() : new Date(subscription.endDate);
        const now = new Date();
        const diffTime = endDate - now;
        daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (daysRemaining > 0) {
          status = 'Actif';
        } else {
          status = 'Expiré';
        }
        endDateFormatted = endDate.toLocaleDateString('fr-FR');
      }

      if (subscription.startDate) {
        const startDate = subscription.startDate.toDate ? subscription.startDate.toDate() : new Date(subscription.startDate);
        startDateFormatted = startDate.toLocaleDateString('fr-FR');
      }

      // Récupérer l'historique des paiements (version simplifiée pour éviter l'erreur d'index)
      let paymentHistory = [];
      
      try {
        // Essayer d'abord sans orderBy pour éviter l'erreur d'index
      const paymentsSnapshot = await db.collection('payments')
          .where('userId', '==', userDoc.id)
        .limit(5)
        .get();

      paymentsSnapshot.forEach(doc => {
        const payment = doc.data();
        paymentHistory.push({
          id: doc.id,
          date: payment.date ? this.formatDate(payment.date) : 'Date inconnue',
            plan: payment.planName || subscription.plan || 'Inconnu',
            amount: `${payment.amount || subscription.amount || 0} ${payment.currency || subscription.currency || 'FCFA'}`,
            status: payment.status || 'Traité'
          });
        });

        // Trier manuellement par date
        paymentHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
        
      } catch (indexError) {
        // Continuer sans historique de paiements
      }

      // Si pas d'historique dans payments, créer un élément depuis subscription
      if (paymentHistory.length === 0 && subscription.amount > 0) {
        paymentHistory.push({
          id: 'current',
          date: startDateFormatted,
          plan: subscription.plan || 'Abonnement actuel',
          amount: `${subscription.amount} ${subscription.currency || 'FCFA'}`,
          status: status === 'Actif' ? 'Actif' : 'Expiré'
        });
      }

      this.setState({
        currentPlan: subscription.plan || 'Gratuit',
        endDate: endDateFormatted,
        startDate: startDateFormatted,
        autoRenew: subscription.autoRenew || false,
        paymentHistory,
        loading: false,
        error: null,
        userInfo: {
          name: userData.name || 'Utilisateur',
          email: userData.email,
          status: status,
          daysRemaining: daysRemaining,
          foundMethod: foundMethod
        }
      });

    } catch (error) {
      this.setState({
        loading: false,
        error: "Erreur lors du chargement des données: " + error.message
      });
    }
  }

  formatDate = (timestamp) => {
    if (!timestamp) return 'Non défini';

    try {
      const date = timestamp.toDate ? timestamp.toDate() :
        timestamp._seconds ? new Date(timestamp._seconds * 1000) :
          new Date(timestamp);

      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return 'Date invalide';
    }
  }

  render() {
    const { currentPlan, endDate, startDate, loading, paymentHistory, error, userInfo } = this.state;

    if (loading) {
      return (
        <div className="subscription-page">
          <div className="subscription-panel subscription-state">
            <span className="subscription-state-icon"><i className="fa fa-spinner fa-spin" /></span>
            <h1>Chargement de votre abonnement</h1>
            <p>Récupération sécurisée de vos informations.</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="subscription-page">
          <div className="subscription-panel subscription-state subscription-error">
            <span className="subscription-state-icon"><i className="fa fa-exclamation-circle" /></span>
            <h1>Abonnement indisponible</h1>
            <p>{error}</p>
            <button className="subscription-button" onClick={this.loadSubscriptionData}>
              <i className="fa fa-refresh" /> Réessayer
            </button>
          </div>
        </div>
      );
    }

    const statusClass = userInfo?.status === 'Actif' ? 'is-active' :
      userInfo?.status === 'Expiré' ? 'is-expired' : 'is-inactive';

    return (
      <div className="subscription-page">
        <main className="subscription-panel">
          <div className="subscription-content">
            {userInfo && (
              <section className="subscription-user">
                <span className="subscription-user-avatar"><i className="fa fa-user" /></span>
                <div className="subscription-user-details">
                  <span className="subscription-eyebrow">COMPTE</span>
                  <h2>{userInfo.name}</h2>
                  <p>{userInfo.email}</p>
                </div>
                <span className={`subscription-status ${statusClass}`}>
                  <i className={`fa fa-${userInfo.status === 'Actif' ? 'check-circle' : 'info-circle'}`} />
                  {userInfo.status}
                </span>
              </section>
            )}

                <section className="subscription-plan">
                  <div className="subscription-plan-heading">
                    <span className="subscription-plan-icon"><i className="fa fa-star" /></span>
                    <div>
                      <span className="subscription-eyebrow">FORMULE ACTUELLE</span>
                      <h2>{currentPlan}</h2>
                    </div>
                    <span className={`subscription-status ${statusClass}`}>{userInfo?.status || 'Inconnu'}</span>
                  </div>

                  <div className="subscription-details-grid">
                    <div className="subscription-detail">
                      <span className="subscription-detail-icon"><i className="fa fa-calendar-check-o" /></span>
                      <div><span>Date de début</span><strong>{startDate}</strong></div>
                    </div>
                    <div className="subscription-detail">
                      <span className="subscription-detail-icon"><i className="fa fa-calendar" /></span>
                      <div><span>Date de fin</span><strong>{endDate}</strong></div>
                    </div>
                    {userInfo?.daysRemaining > 0 && (
                      <div className="subscription-detail">
                        <span className="subscription-detail-icon"><i className="fa fa-clock-o" /></span>
                        <div><span>Validité restante</span><strong>{userInfo.daysRemaining} jours</strong></div>
                      </div>
                    )}
                    <div className="subscription-detail">
                      <span className="subscription-detail-icon"><i className="fa fa-refresh" /></span>
                      <div><span>Renouvellement automatique</span><strong>{this.state.autoRenew ? 'Activé' : 'Désactivé'}</strong></div>
                    </div>
                    {paymentHistory.length > 0 && (
                      <div className="subscription-detail">
                        <span className="subscription-detail-icon"><i className="fa fa-credit-card" /></span>
                        <div><span>Dernier paiement</span><strong>{paymentHistory[0].amount}</strong></div>
                      </div>
                    )}
                  </div>

                  <div className="subscription-plan-actions">
                    <button
                      className="subscription-button"
                      onClick={() => { window.location.href = '/abonnement'; }}
                    >
                      <i className={`fa fa-${userInfo?.status === 'Actif' ? 'cog' : 'credit-card'}`} />
                      {userInfo?.status === 'Actif' ? "Modifier l'abonnement" : "Renouveler l'abonnement"}
                    </button>
                  </div>
                </section>

                <section className="subscription-history">
                  <div className="subscription-section-heading">
                    <div>
                      <span className="subscription-eyebrow">FACTURATION</span>
                      <h2>Historique des paiements</h2>
                    </div>
                    <span className="subscription-history-count">{paymentHistory.length} paiement(s)</span>
                  </div>

                  {paymentHistory.length > 0 ? (
                    <div className="subscription-table-wrap">
                      <table className="subscription-table">
                        <thead>
                          <tr><th>Date</th><th>Formule</th><th>Montant</th><th>Statut</th></tr>
                        </thead>
                        <tbody>
                          {paymentHistory.map((payment, index) => (
                            <tr key={payment.id || index}>
                              <td>{payment.date}</td>
                              <td>{payment.plan}</td>
                              <td className="subscription-amount">{payment.amount}</td>
                              <td>
                                <span className={`subscription-payment-status ${payment.status === 'Actif' || payment.status === 'Traité' ? 'is-paid' : ''}`}>
                                  {payment.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="subscription-empty">
                      <i className="fa fa-file-text-o" />
                      <span>Aucun paiement enregistré pour le moment.</span>
                    </div>
                  )}
                </section>

                <footer className="subscription-footer">
                  <span>Vos informations d’abonnement sont mises à jour depuis votre compte.</span>
                  <button
                    className="subscription-text-button"
                    onClick={this.loadSubscriptionData}
                    disabled={loading}
                  >
                    <i className="fa fa-refresh" /> Actualiser les informations
                  </button>
                </footer>
          </div>
        </main>
      </div>
    );
  }
}

SubscriptionManagement.propTypes = {
  user: PropTypes.object,
  profile: PropTypes.object
};

const mapStateToProps = (state) => ({
  user: state.user.user,
    profile: state.user.profile
});

export default connect(mapStateToProps)(SubscriptionManagement);